
import React from 'react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Auth from './components/Auth';
import { AppShell, LegacyPage } from './components/layout/AppShell';
import { canAccessPreceptor } from './services/authService';

const ErrorFallback: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen bg-[var(--color-background)] items-center justify-center">
      <div className="text-center max-w-md p-8">
        <i className="fas fa-exclamation-triangle text-5xl text-red-500 mb-4"></i>
        <h1 className="text-2xl font-bold text-[var(--color-text)] mb-2">
          {t('errors.unexpected')}
        </h1>
        <p className="text-[var(--color-muted)] mb-6">
          {t('errors.unexpectedDesc')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-[var(--color-accent)] text-[var(--color-background)] font-bold rounded-lg hover:bg-[var(--color-accent)]/80 transition-colors"
        >
          {t('errors.reloadPage')}
        </button>
      </div>
    </div>
  );
};

const Settings = React.lazy(() => import('./components/Settings'));
const HomeView = React.lazy(() => import('./components/news/HomeView'));
const FeedView = React.lazy(() => import('./components/news/FeedView'));
const TemasView = React.lazy(() => import('./components/news/TemasView'));
const ArticlePage = React.lazy(() => import('./components/news/ArticlePage'));
const BibliotecaView = React.lazy(() => import('./components/news/BibliotecaView'));
const AdminContents = React.lazy(() => import('./components/admin/AdminContents'));
const AdminContentEditor = React.lazy(() => import('./components/admin/AdminContentEditor'));
const Flashcards = React.lazy(() => import('./components/Flashcards'));
const FlashcardSession = React.lazy(() => import('./components/FlashcardSession'));
const PreceptorAI = React.lazy(() => import('./components/PreceptorAI'));
const AdminUsers = React.lazy(() => import('./components/AdminUsers'));
const AdminLegalAcceptances = React.lazy(
  () => import('./components/admin/AdminLegalAcceptances'),
);

// Produtos em espanhol. Espelha SPANISH_PRODUCT_IDS em
// backend-neuronews/src/common/content-lang.ts — mantenha os dois em sincronia.
const SPANISH_PRODUCT_IDS = [1, 6];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const ProtectedLayout: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { i18n } = useTranslation();

  React.useEffect(() => {
    const lang = SPANISH_PRODUCT_IDS.includes(user?.productId ?? -1) ? 'es' : 'pt-BR';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
    }
  }, [user?.productId, i18n]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-[var(--color-background)] items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-5xl text-[var(--color-accent)] mb-4"></i>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Auth />;
  }

  return <AppShell />;
};

const AdminRoute: React.FC = () => {
  const { user } = useAuth();
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

const PreceptorRoute: React.FC = () => {
  const { user } = useAuth();
  if (!canAccessPreceptor(user)) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<ProtectedLayout />}>
                <Route path="/" element={<HomeView />} />
                <Route path="/feed" element={<FeedView />} />
                <Route path="/temas" element={<TemasView />} />
                <Route path="/biblioteca" element={<BibliotecaView />} />
                <Route path="/articles" element={<Navigate to="/feed" replace />} />
                <Route path="/articles/:slug" element={<ArticlePage />} />
                <Route path="/flashcards" element={<LegacyPage><Flashcards /></LegacyPage>} />
                <Route path="/flashcards/session" element={<LegacyPage><FlashcardSession /></LegacyPage>} />
                <Route element={<PreceptorRoute />}>
                  <Route path="/preceptor" element={<LegacyPage><PreceptorAI /></LegacyPage>} />
                </Route>
                <Route element={<AdminRoute />}>
                  <Route path="/admin/users" element={<LegacyPage><AdminUsers /></LegacyPage>} />
                  <Route path="/admin/termos" element={<LegacyPage><AdminLegalAcceptances /></LegacyPage>} />
                  <Route path="/admin/conteudos" element={<AdminContents />} />
                  <Route path="/admin/conteudos/novo" element={<AdminContentEditor />} />
                  <Route path="/admin/conteudos/:grupoId" element={<AdminContentEditor />} />
                </Route>
                <Route path="/settings" element={<LegacyPage><Settings /></LegacyPage>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
